import { useEffect } from 'react';
import ErrorBoundary from './error/ErrorBoundary';
import { EmotionCacheProvider } from './providers/EmotionCacheProvider';
import { QueryProvider } from './providers/QueryProvider';
import { UIProvider } from './providers/UIProvider';
import { LegacyPermissionsProvider } from './lib/useLegacyPermissions';
import { StandaloneBootstrap } from './standalone/StandaloneBootstrap';
import { usePropertyStore } from './store/propertyStore';
import { useAppGlobalsStore } from './store/appGlobalsStore';
import { useCompanyStore } from './store/companyStore';
import { useNavContextStore } from './store/navContextStore';
import { useWdsStore } from './store/wdsStore';
import { useRctStore } from './store/rctStore';
import { useServiceAreaStore } from './store/serviceAreaStore';
import { useWorkOrderWorkflowSync } from './hooks/useWorkOrderWorkflowSync';
import { getMountConfig } from './mountConfig';
import RootRouter from './routes/RootRouter';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * Hydrates the property store from AngularJS $rootScope on mount (embedded mode).
 * Polls every 500ms to pick up AngularJS search widget changes.
 */
function usePropertyStoreHydration() {
  const hydrateFromLegacy = usePropertyStore((s) => s.hydrateFromLegacy);

  useEffect(() => {
    const { mode } = getMountConfig();
    if (mode !== 'embedded') return;

    // Initial hydration
    hydrateFromLegacy();

    // Poll for AngularJS changes (search widget, sidebar navigation, etc.)
    const interval = setInterval(hydrateFromLegacy, 500);
    return () => clearInterval(interval);
  }, [hydrateFromLegacy]);
}

/**
 * Hydrates the app-globals store from AngularJS `$rootScope.G_*` on mount
 * (embedded mode). Re-reads on a slower cadence than the property store
 * because these globals change rarely (settings tweaks, navigation events)
 * vs work-order context which can flip on every search.
 */
function useAppGlobalsHydration() {
  const hydrateFromLegacy = useAppGlobalsStore((s) => s.hydrateFromLegacy);

  useEffect(() => {
    const { mode } = getMountConfig();
    if (mode !== 'embedded') return;

    hydrateFromLegacy();
    const interval = setInterval(hydrateFromLegacy, 1500);
    return () => clearInterval(interval);
  }, [hydrateFromLegacy]);
}

/**
 * Hydrates the secondary domain stores (company, navigation, WDS, RCT, service
 * area) from AngularJS `$rootScope.*` on mount in embedded mode. Re-reads at
 * the same cadence as app-globals so badges and the sub-nav stay in sync.
 */
function useSecondaryStoreHydration() {
  const hydrateCompany = useCompanyStore((s) => s.hydrateFromLegacy);
  const hydrateNav = useNavContextStore((s) => s.hydrateFromLegacy);
  const hydrateWds = useWdsStore((s) => s.hydrateFromLegacy);
  const hydrateRct = useRctStore((s) => s.hydrateFromLegacy);
  const hydrateServiceArea = useServiceAreaStore((s) => s.hydrateFromLegacy);

  useEffect(() => {
    const { mode } = getMountConfig();
    if (mode !== 'embedded') return;

    const hydrateAll = () => {
      hydrateCompany();
      hydrateNav();
      hydrateWds();
      hydrateRct();
      hydrateServiceArea();
    };

    hydrateAll();
    const interval = setInterval(hydrateAll, 1500);
    return () => clearInterval(interval);
  }, [hydrateCompany, hydrateNav, hydrateWds, hydrateRct, hydrateServiceArea]);
}

/**
 * Suppress browser navigation for anchor tags whose href is empty (`href=""`)
 * or just a fragment (`href="#"`). The legacy AngularJS templates used these
 * heavily as Bootstrap-styled buttons (dropdown toggles, sortable column
 * headers, etc.) and relied on `$location` ignoring an empty href. HashRouter
 * does not — it resolves to `/` and the root route then `Navigate`s to
 * `/Home/dashboard`, which is what users were seeing on Admin → Allowables →
 * Uploads, VendorManagement → Admin → Unlock User, and ~110 other places.
 *
 * We only preventDefault — the onClick handler still runs (the listener is
 * registered in the bubble phase, after React's onClick has dispatched), so
 * dropdown toggles continue to open and sort handlers continue to fire.
 */
function useEmptyHrefInterceptor() {
  useEffect(() => {
    function onAnchorClick(event: MouseEvent) {
      const target = event.target as Element | null;
      const anchor = target?.closest?.('a') as HTMLAnchorElement | null;
      if (!anchor) return;
      // `getAttribute('href')` returns the raw attribute. `anchor.href` would
      // resolve relative URLs and never be empty even for `href=""`.
      const raw = anchor.getAttribute('href');
      if (raw === '' || raw === '#') {
        event.preventDefault();
      }
    }
    document.addEventListener('click', onAnchorClick);
    return () => document.removeEventListener('click', onAnchorClick);
  }, []);
}

function App() {
  usePropertyStoreHydration();
  useAppGlobalsHydration();
  useSecondaryStoreHydration();
  useWorkOrderWorkflowSync();
  useEmptyHrefInterceptor();

  return (
    <ErrorBoundary>
      <EmotionCacheProvider>
        <QueryProvider>
          <LegacyPermissionsProvider>
            <UIProvider>
              <StandaloneBootstrap>
                <RootRouter />
              </StandaloneBootstrap>
              <ToastContainer
                position='bottom-right'
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                limit={2}
              />
            </UIProvider>
          </LegacyPermissionsProvider>
        </QueryProvider>
      </EmotionCacheProvider>
    </ErrorBoundary>
  );
}

export default App;
